import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import React, { Suspense } from "react";
import { FullDevice } from "src/api/generated/frontend_internal_v1";
import { UUID } from "src/api/api_common";
import { ApiInternal } from "src/api/api_internal";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { Button } from "src/components/base/button";
import { LinkSlashIcon } from "@heroicons/react/20/solid";
import { Dialog, DialogActions, DialogDescription, DialogTitle } from "src/components/base/dialog";
import SINGLE_DEVICE_GROUP_CONTEXT from "src/context/internal/device-group";
import { Text } from "src/components/base/text";

/**
 * The properties for {@link AssociatedDevices}
 */
export type AssociatedDevicesProps = {};

/**
 * A list of all associated devices
 */
function AssociatedDevices(props: AssociatedDevicesProps) {
    const [t] = useTranslation("admin-device-group");
    const [tg] = useTranslation();

    const [openUnlinkDevice, setOpenUnlinkDevice] = React.useState<FullDevice>();

    const deviceGroupContext = React.useContext(SINGLE_DEVICE_GROUP_CONTEXT);

    /**
     * Unlink a device from this device group
     *
     * @param device The device to unlink
     */
    const unlinkDevice = async (device: UUID) => {
        await ApiInternal.devices.removeFromDeviceGroup(device, deviceGroupContext.deviceGroup.uuid);
        deviceGroupContext.reset();
    };

    if (deviceGroupContext.deviceGroup.devices.length === 0) {
        return <Text>{t("description.no-devices-in-device-group")}</Text>;
    }

    return (
        <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.device-name")}</TableHeader>
                        <TableHeader className={"w-0"}>
                            <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {deviceGroupContext.deviceGroup.devices.map((device) => (
                        <TableRow
                            key={device.uuid}
                            href={"/a/devices/$deviceId/general"}
                            params={{ deviceId: device.uuid }}
                        >
                            <TableCell>{device.dnsname}</TableCell>
                            <TableCell>
                                <Button plain={true} onClick={() => setOpenUnlinkDevice(device)}>
                                    <LinkSlashIcon />
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {openUnlinkDevice && (
                <Suspense>
                    <Dialog open={true} onClose={() => setOpenUnlinkDevice(undefined)}>
                        <DialogTitle>{t("heading.unlink-device", { device: openUnlinkDevice.dnsname })}</DialogTitle>
                        <DialogDescription>{t("description.unlink-device")}</DialogDescription>
                        <DialogActions>
                            <Button onClick={() => setOpenUnlinkDevice(undefined)}>{tg("button.cancel")}</Button>
                            <Button
                                color={"red"}
                                onClick={async () => {
                                    await unlinkDevice(openUnlinkDevice?.uuid);
                                    setOpenUnlinkDevice(undefined);
                                }}
                            >
                                {t("button.unlink-device")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Suspense>
            )}
        </>
    );
}

export const Route = createFileRoute("/_admin/a/devicegroups/$deviceGroupId/_devicegroups/associated")({
    component: AssociatedDevices,
});
